import { motion, useMotionValue, useTransform } from 'motion/react';
import { memo, useEffect, useState } from 'react';
import { useAtomMotion } from '../../contexts/MotionContext';
import { useFormatOverride } from '../../rendering/contexts/FormatOverrideContext';
import { computeBondEndPoints } from '@alchemiesolns/bond-calc-pure';
import type { BondRecipe } from '../../rendering/types/bondRecipe.types';
import { useFeedbackWithContext } from '../../feedback';
import { useSelection } from '../../selection';

// Gap size between dual hitbox sides at midpoint (in SVG units)
const DUAL_BOND_GAP = .1;

/**
 * Creates an SVG path for a rectangle with rounded corners on one end only
 * @param x - Top-left x coordinate
 * @param y - Top-left y coordinate
 * @param width - Rectangle width
 * @param height - Rectangle height
 * @param roundedSide - Which side should be rounded ('left' or 'right')
 * @returns SVG path string
 */
function createHalfRoundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  roundedSide: 'left' | 'right'
): string {
  const radius = height / 2; // Use full height for maximum roundness

  if (roundedSide === 'left') {
    // Rounded on left end, square on right end
    // Start at top-left (after the curve)
    const startX = x + radius;
    const startY = y;

    return `
      M ${startX} ${startY}
      L ${x + width} ${y}
      L ${x + width} ${y + height}
      L ${startX} ${y + height}
      Q ${x} ${y + height} ${x} ${y + height - radius}
      L ${x} ${y + radius}
      Q ${x} ${y} ${startX} ${y}
      Z
    `.trim().replace(/\s+/g, ' ');
  } else {
    // Rounded on right end, square on left end
    const endX = x + width - radius;

    return `
      M ${x} ${y}
      L ${endX} ${y}
      Q ${x + width} ${y} ${x + width} ${y + radius}
      L ${x + width} ${y + height - radius}
      Q ${x + width} ${y + height} ${endX} ${y + height}
      L ${x} ${y + height}
      Z
    `.trim().replace(/\s+/g, ' ');
  }
}

export interface DynamicDualBondHitboxProps {
  recipe: BondRecipe;
  onClick?: (e: React.MouseEvent<SVGElement>, bondId: string) => void;
  onMouseEnter?: (bondId: string) => void;
  onMouseLeave?: () => void;
  highlightEnd?: 'atom1' | 'atom2' | null;
}

export const DynamicDualBondHitbox = memo(function DynamicDualBondHitbox({
  recipe,
  onClick,
  onMouseEnter,
  onMouseLeave,
  highlightEnd,
}: DynamicDualBondHitboxProps) {
  const selection = useSelection({ optional: true });
  const format = useFormatOverride();
  const feedback = useFeedbackWithContext(recipe.bondId);

  // Get motion values for both atoms
  const atom1Motion = useAtomMotion(recipe.atom1Id);
  const atom2Motion = useAtomMotion(recipe.atom2Id);

  // If either atom has no motion values, don't render
  if (!atom1Motion || !atom2Motion) {
    return null;
  }

  // Check selection state (matches DynamicAtom behavior)
  const isSelected = selection?.isSelected(recipe.bondId) ?? false;
  const isHovered = selection?.isHovered(recipe.bondId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  // Get opacity from FeedbackManager
  const opacity = feedback.currentStyle.opacity ?? 0;
  const fillOpacity = feedback.currentStyle.fillOpacity ?? 0;

    // Default interaction handlers
    const handleClick = (e: React.MouseEvent<SVGElement>) => {
      e.stopPropagation();
      if (onClick) {
        onClick(e, recipe.bondId);
      }
    };

    const handleMouseEnter = () => {
      if (onMouseEnter) {
        onMouseEnter(recipe.bondId);
      } else if (selection) {
        selection.setHovered(recipe.bondId);
      }
    };

    const handleMouseLeave = () => {
      if (onMouseLeave) {
        onMouseLeave();
      } else if (selection) {
        selection.setHovered(null);
      }
    };

  // Render dual hitbox for each tube/line in the bond
  return (
    <>
        <DynamicDualBondHitboxLine
          key={`${recipe.bondId}-dual-hitbox-${recipe.tubes[0].lineIndex}`}
          recipe={recipe}
          tube={recipe.tubes[0]}
          atom1Motion={atom1Motion}
          atom2Motion={atom2Motion}
          format={format}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          opacity={opacity}
          fillOpacity={fillOpacity}
          highlightEnd={highlightEnd}
          feedback={feedback}
        />
    </>
  );
});

/**
 * Individual dual hitbox line that tracks atom motion and renders two side-by-side zones
 */
function DynamicDualBondHitboxLine({
  recipe,
  tube,
  atom1Motion,
  atom2Motion,
  format,
  onClick,
  onMouseEnter,
  onMouseLeave,
  highlightEnd,
  opacity,
  fillOpacity,
  feedback,
}: {
  recipe: BondRecipe;
  tube: any;
  atom1Motion: any;
  atom2Motion: any;
  format: any;
  onClick?: (e: React.MouseEvent<SVGElement>, bondId: string) => void;
  onMouseEnter?: (bondId: string) => void;
  onMouseLeave?: () => void;
  highlightEnd?: 'atom1' | 'atom2' | null;
  opacity: number;
  fillOpacity: number;
  feedback: any;
}) {
  // Track which side is currently being hovered (1, 2, or null)
  const [hoveredSide, setHoveredSide] = useState<1 | 2 | null>(null);

  // Motion values for bond endpoints
  const x1 = useMotionValue(0);
  const y1 = useMotionValue(0);
  const x2 = useMotionValue(0);
  const y2 = useMotionValue(0);

  // Motion values for midpoint (split point)
  const midX = useTransform([x1, x2], ([a, b]) => ((a as number) + (b as number)) / 2);
  const midY = useTransform([y1, y2], ([a, b]) => ((a as number) + (b as number)) / 2);

  // Calculate gap-adjusted endpoints for dual sides
  // Both sides start at midpoint +/- offset toward their respective atoms
  // Side 1: starts at midpoint + offset toward atom1
  const side1StartX = useTransform([midX, x1, y1], ([mx, x1Val, y1Val]) => {
    const dx = (x1Val as number) - (mx as number);
    const dy = (y1Val as number) - (midY.get());
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.001) return mx as number; // Prevent division by zero
    return (mx as number) + (dx / length) * (DUAL_BOND_GAP / 2);
  });

  const side1StartY = useTransform([midY, x1, y1], ([my, x1Val, y1Val]) => {
    const dx = (x1Val as number) - (midX.get());
    const dy = (y1Val as number) - (my as number);
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.001) return my as number;
    return (my as number) + (dy / length) * (DUAL_BOND_GAP / 2);
  });

  // Side 2: starts at midpoint + offset toward atom2
  const side2StartX = useTransform([midX, x2, y2], ([mx, x2Val, y2Val]) => {
    const dx = (x2Val as number) - (mx as number);
    const dy = (y2Val as number) - (midY.get());
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.001) return mx as number;
    return (mx as number) + (dx / length) * (DUAL_BOND_GAP / 2);
  });

  const side2StartY = useTransform([midY, x2, y2], ([my, x2Val, y2Val]) => {
    const dx = (x2Val as number) - (midX.get());
    const dy = (y2Val as number) - (my as number);
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.001) return my as number;
    return (my as number) + (dy / length) * (DUAL_BOND_GAP / 2);
  });

  // Update endpoint positions when atoms move
  useEffect(() => {
    const updateEndpoints = () => {
      const atom1X = atom1Motion.x.get();
      const atom1Y = atom1Motion.y.get();
      const atom2X = atom2Motion.x.get();
      const atom2Y = atom2Motion.y.get();

      const a1Pos = [atom1X, atom1Y, 0];
      const a2Pos = [atom2X, atom2Y, 0];

      // Calculate endpoints with perpendicular offset
      const endPoints = computeBondEndPoints(a1Pos, a2Pos, [0, 0, 1], 0);
      x1.set(endPoints.a1[0]);
      y1.set(endPoints.a1[1]);
      x2.set(endPoints.a2[0]);
      y2.set(endPoints.a2[1]);
    };

    // Initial update
    updateEndpoints();

    // Subscribe to atom position changes
    const unsubscribe1X = atom1Motion.x.on('change', updateEndpoints);
    const unsubscribe1Y = atom1Motion.y.on('change', updateEndpoints);
    const unsubscribe2X = atom2Motion.x.on('change', updateEndpoints);
    const unsubscribe2Y = atom2Motion.y.on('change', updateEndpoints);

    return () => {
      unsubscribe1X();
      unsubscribe1Y();
      unsubscribe2X();
      unsubscribe2Y();
    };
  }, [atom1Motion, atom2Motion, tube, x1, y1, x2, y2]);

  // Click handlers
  const handleClick = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e, recipe.bondId);
    }
  };

  // Per-side mouse handlers
  const handleSide1MouseEnter = () => {
    setHoveredSide(1);
    if (onMouseEnter) {
      onMouseEnter(recipe.bondId);
    }
  };

  const handleSide2MouseEnter = () => {
    setHoveredSide(2);
    if (onMouseEnter) {
      onMouseEnter(recipe.bondId);
    }
  };

  const handleMouseLeave = () => {
    setHoveredSide(null);
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  const hitboxWidth = format.bond.thickness + (feedback.currentStyle.bondThickness ?? 2.6) - 0.2;
  const atomRadius = format.atom.radius;

  // Calculate opacity based on selection/hover state or highlightEnd
  // Priority: highlightEnd (from DraggableBond during pointer move) > hoveredSide (internal per-side tracking)
  // Only one side should be visible at a time
  const side1Opacity = highlightEnd
    ? (highlightEnd === 'atom1' ? opacity : 0)
    : (hoveredSide === 1 ? opacity : 0);
  const side2Opacity = highlightEnd
    ? (highlightEnd === 'atom2' ? opacity : 0)
    : (hoveredSide === 2 ? opacity : 0);

    const side1FillOpacity = highlightEnd
    ? (highlightEnd === 'atom1' ? fillOpacity : 0)
    : (hoveredSide === 1 ? fillOpacity : 0);
  const side2FillOpacity = highlightEnd
    ? (highlightEnd === 'atom2' ? fillOpacity : 0)
    : (hoveredSide === 2 ? fillOpacity : 0);

  // Calculate side 1 base length (from gap-adjusted midpoint to atom1)
  const side1BaseLength = useTransform([side1StartX, side1StartY, x1, y1], (latest) => {
    const [startX, startY, endX, endY] = latest as number[];
    const dx = endX - startX;
    const dy = endY - startY;
    return Math.sqrt(dx * dx + dy * dy);
  });

  // Extend side 1 length to include atom radius at atom1 end
  const side1Length = useTransform(
    side1BaseLength,
    (length) => (length as number) + atomRadius
  );

  // Calculate side 1 center point (shifted by atomRadius/2 toward atom1)
  const side1CenterX = useTransform([side1StartX, side1StartY, x1, y1], (latest) => {
    const [startX, startY, endX, endY] = latest as number[];
    const dx = endX - startX;
    const dy = endY - startY;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.001) return (startX + endX) / 2; // Prevent division by zero
    const unitX = dx / length;
    const midX = (startX + endX) / 2;
    return midX + (unitX * atomRadius / 2);
  });
  const side1CenterY = useTransform([side1StartX, side1StartY, x1, y1], (latest) => {
    const [startX, startY, endX, endY] = latest as number[];
    const dx = endX - startX;
    const dy = endY - startY;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.001) return (startY + endY) / 2;
    const unitY = dy / length;
    const midY = (startY + endY) / 2;
    return midY + (unitY * atomRadius / 2);
  });

  // Calculate side 1 rotation angle
  const side1Angle = useTransform([side1StartX, side1StartY, x1, y1], (latest) => {
    const [startX, startY, endX, endY] = latest as number[];
    const dx = endX - startX;
    const dy = endY - startY;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  });

  // Calculate side 2 base length (from gap-adjusted midpoint to atom2)
  const side2BaseLength = useTransform([side2StartX, side2StartY, x2, y2], (latest) => {
    const [startX, startY, endX, endY] = latest as number[];
    const dx = endX - startX;
    const dy = endY - startY;
    return Math.sqrt(dx * dx + dy * dy);
  });

  // Extend side 2 length to include atom radius at atom2 end
  const side2Length = useTransform(
    side2BaseLength,
    (length) => (length as number) + atomRadius
  );

  // Calculate side 2 center point (shifted by atomRadius/2 toward atom2)
  const side2CenterX = useTransform([side2StartX, side2StartY, x2, y2], (latest) => {
    const [startX, startY, endX, endY] = latest as number[];
    const dx = endX - startX;
    const dy = endY - startY;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.001) return (startX + endX) / 2;
    const unitX = dx / length;
    const midX = (startX + endX) / 2;
    return midX + (unitX * atomRadius / 2);
  });
  const side2CenterY = useTransform([side2StartX, side2StartY, x2, y2], (latest) => {
    const [startX, startY, endX, endY] = latest as number[];
    const dx = endX - startX;
    const dy = endY - startY;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.001) return (startY + endY) / 2;
    const unitY = dy / length;
    const midY = (startY + endY) / 2;
    return midY + (unitY * atomRadius / 2);
  });

  // Calculate side 2 rotation angle
  const side2Angle = useTransform([side2StartX, side2StartY, x2, y2], (latest) => {
    const [startX, startY, endX, endY] = latest as number[];
    const dx = endX - startX;
    const dy = endY - startY;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  });

  // Render with rectangles instead of lines for better hit detection
  return (
    <>
      {/* Side 1: Rectangle hitbox with left-side rounding using FeedbackManager styling */}
      <motion.path
        className="svg-bond-hitbox svg-bond-hitbox-dual-side1"
        data-bond-id={recipe.bondId}
        data-bond-index={String(recipe.bondIndex)}
        data-atom1-id={recipe.atom1Id}
        data-atom2-id={recipe.atom2Id}
        data-side="1"
        d={useTransform([side1CenterX, side1CenterY, side1Length], (latest) => {
          const [cx, cy, length] = latest as number[];
          const x = cx - length / 2;
          const y = cy - hitboxWidth / 2;
          return createHalfRoundedRectPath(x, y, length, hitboxWidth, 'right');
        })}
        fill={feedback.currentStyle.bondColor ?? '#90bcd0'}
        fillOpacity={side1FillOpacity}
        opacity={side1Opacity}
        stroke={feedback.currentStyle.hitboxStroke}
        strokeWidth={feedback.currentStyle.bondStrokeWidth ?? 0}
        style={{
          cursor: feedback.currentStyle.cursor ?? 'pointer',
          transformOrigin: 'center',
          rotate: side1Angle,
        }}
        onClick={handleClick}
        onMouseEnter={handleSide1MouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      {/* Side 2: Rectangle hitbox with right-side rounding using FeedbackManager styling */}
      <motion.path
        className="svg-bond-hitbox svg-bond-hitbox-dual-side2"
        data-bond-id={recipe.bondId}
        data-bond-index={String(recipe.bondIndex)}
        data-atom1-id={recipe.atom1Id}
        data-atom2-id={recipe.atom2Id}
        data-side="2"
        d={useTransform([side2CenterX, side2CenterY, side2Length], (latest) => {
          const [cx, cy, length] = latest as number[];
          const x = cx - length / 2;
          const y = cy - hitboxWidth / 2;
          return createHalfRoundedRectPath(x, y, length, hitboxWidth, 'right');
        })}
        fill={feedback.currentStyle.bondColor ?? '#90bcd0'}
        fillOpacity={side2FillOpacity}
        opacity={side2Opacity}
        stroke={feedback.currentStyle.hitboxStroke}
        strokeWidth={feedback.currentStyle.bondStrokeWidth ?? 0}
        style={{
          cursor: feedback.currentStyle.cursor ?? 'pointer',
          transformOrigin: 'center',
          rotate: side2Angle,
        }}
        onClick={handleClick}
        onMouseEnter={handleSide2MouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </>
  );
}
